export const METRIC_RANGE_KEYS = [
  'value',
  'avg',
  'average',
  'min',
  'max',
  'count',
  'sum',
  'median',
  'variance',
  'stddev',
] as const;

export const PRIMARY_RANGE_KEYS = [
  'value',
  'avg',
  'average',
  'min',
  'max',
  'sum',
  'median',
] as const;

export const COUNT_RANGE_KEYS = ['count'] as const;
export const VARIANCE_STDDEV_RANGE_KEYS = ['variance', 'stddev'] as const;
export const BENCHMARK_BAND_KEYS = ['1', '2', '3', '4', '5'] as const;
export const BENCHMARK_BAND_LABELS: Record<(typeof BENCHMARK_BAND_KEYS)[number], string> = {
  '1': 'Poor',
  '2': 'Fair',
  '3': 'Good',
  '4': 'Great',
  '5': 'Perfect',
};

export const SIMPLIFIED_RANGE_KEYS = ['value', 'count', 'variance'] as const;

export const METRIC_RULE_RANGE_GROUPS = [
  'warningRange',
  'colorRange',
  'notificationRange',
  'healthStatusRange',
  'benchmarkRange',
] as const;

export type MetricRange = {
  min?: number | null;
  max?: number | null;
};

export type MetricRangeMap = Record<string, MetricRange>;

export type MetricNotificationLogic = {
  minViolatingAggregations?: number | null;
};

export type MetricHealthStatusLogic = {
  attentionMinOutOfRangeCount?: number | null;
  actionNeededMinOutOfRangePercent?: number | null;
};

export type MetricRuleSet = {
  warningRange?: MetricRangeMap;
  colorRange?: MetricRangeMap;
  notificationRange?: MetricRangeMap;
  healthStatusRange?: MetricRangeMap;
  benchmarkRange?: MetricRangeMap;
  notificationLogic?: MetricNotificationLogic;
  healthStatusLogic?: MetricHealthStatusLogic;
};

export type MetricRuleSetMap = Record<string, MetricRuleSet>;
export type AreaTypeMetricRuleSetMap = Record<string, MetricRuleSetMap>;

const hasOwn = (obj: unknown, key: string) =>
  !!obj && typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, key);

const toNullableNumber = (value: unknown, present: boolean): number | null | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (present) return null;
  return undefined;
};

const cleanRangeEntry = (raw: unknown): MetricRange | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const min = toNullableNumber((raw as any).min, hasOwn(raw, 'min'));
  const max = toNullableNumber((raw as any).max, hasOwn(raw, 'max'));

  if (min === undefined && max === undefined) return undefined;
  if (min === null && max === null) return undefined;

  const result: MetricRange = {};
  if (min !== undefined) result.min = min;
  if (max !== undefined) result.max = max;
  return result;
};

export const cleanMetricRangeMap = (raw: unknown): MetricRangeMap | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const cleaned: MetricRangeMap = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    const range = cleanRangeEntry(value);
    if (range) {
      cleaned[key] = range;
    }
  });

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

export const cleanBenchmarkBandRangeMap = (raw: unknown): MetricRangeMap | undefined => {
  const cleaned = cleanMetricRangeMap(raw);
  if (!cleaned) return undefined;

  const next: MetricRangeMap = {};
  BENCHMARK_BAND_KEYS.forEach((key) => {
    const range = cleaned[key];
    if (range) {
      next[key] = range;
    }
  });

  return Object.keys(next).length > 0 ? next : undefined;
};

const cleanNotificationLogic = (raw: unknown): MetricNotificationLogic | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const minViolatingAggregations =
    typeof (raw as any).minViolatingAggregations === 'number' && Number.isFinite((raw as any).minViolatingAggregations)
      ? Math.max(1, Math.round((raw as any).minViolatingAggregations))
      : undefined;

  if (minViolatingAggregations === undefined) return undefined;

  return {
    minViolatingAggregations,
  };
};

const cleanHealthStatusLogic = (raw: unknown): MetricHealthStatusLogic | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const attentionMinOutOfRangeCount =
    typeof (raw as any).attentionMinOutOfRangeCount === 'number' &&
    Number.isFinite((raw as any).attentionMinOutOfRangeCount)
      ? Math.max(1, Math.round((raw as any).attentionMinOutOfRangeCount))
      : undefined;

  const actionNeededMinOutOfRangePercentRaw = (raw as any).actionNeededMinOutOfRangePercent;
  const actionNeededMinOutOfRangePercent =
    typeof actionNeededMinOutOfRangePercentRaw === 'number' && Number.isFinite(actionNeededMinOutOfRangePercentRaw)
      ? Math.min(1, Math.max(0, actionNeededMinOutOfRangePercentRaw))
      : undefined;

  if (attentionMinOutOfRangeCount === undefined && actionNeededMinOutOfRangePercent === undefined) {
    return undefined;
  }

  return {
    attentionMinOutOfRangeCount,
    actionNeededMinOutOfRangePercent,
  };
};

export const cleanMetricRuleSet = (raw: unknown): MetricRuleSet | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const cleaned: MetricRuleSet = {};
  METRIC_RULE_RANGE_GROUPS.forEach((group) => {
    const next =
      group === 'benchmarkRange'
        ? cleanBenchmarkBandRangeMap((raw as Record<string, unknown>)[group])
        : cleanMetricRangeMap((raw as Record<string, unknown>)[group]);
    if (next) {
      cleaned[group] = next;
    }
  });

  const notificationLogic = cleanNotificationLogic((raw as any).notificationLogic);
  if (notificationLogic) {
    cleaned.notificationLogic = notificationLogic;
  }

  const healthStatusLogic = cleanHealthStatusLogic((raw as any).healthStatusLogic);
  if (healthStatusLogic) {
    cleaned.healthStatusLogic = healthStatusLogic;
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

export const cleanMetricRuleSetMap = (raw: unknown): MetricRuleSetMap | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const cleaned: MetricRuleSetMap = {};
  Object.entries(raw as Record<string, unknown>).forEach(([metricKey, value]) => {
    const nextRuleSet = cleanMetricRuleSet(value);
    if (nextRuleSet) {
      cleaned[metricKey] = nextRuleSet;
    }
  });

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

export const cleanAreaTypeMetricRuleSetMap = (
  raw: unknown
): AreaTypeMetricRuleSetMap | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const cleaned: AreaTypeMetricRuleSetMap = {};
  Object.entries(raw as Record<string, unknown>).forEach(([areaTypeId, value]) => {
    const cleanAreaTypeId = String(areaTypeId || '').trim();
    if (!cleanAreaTypeId) return;
    const next = cleanMetricRuleSetMap(value);
    if (next) {
      cleaned[cleanAreaTypeId] = next;
    }
  });

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

const pickFirstRangeFromKeys = (
  rangeMap: MetricRangeMap | undefined,
  keys: readonly string[]
): MetricRange | undefined => {
  if (!rangeMap) return undefined;
  for (const key of keys) {
    const range = cleanRangeEntry(rangeMap[key]);
    if (range) {
      return range;
    }
  }
  return undefined;
};

export const collapseRangeMapForSimplifiedEditing = (
  rangeMap: MetricRangeMap | undefined
): MetricRangeMap | undefined => {
  const primary = pickFirstRangeFromKeys(rangeMap, PRIMARY_RANGE_KEYS);
  const countRange = pickFirstRangeFromKeys(rangeMap, COUNT_RANGE_KEYS);
  const varianceRange = pickFirstRangeFromKeys(rangeMap, VARIANCE_STDDEV_RANGE_KEYS);

  const simplified: MetricRangeMap = {};
  if (primary) {
    simplified.value = { ...primary };
  }
  if (countRange) {
    simplified.count = { ...countRange };
  }
  if (varianceRange) {
    simplified.variance = { ...varianceRange };
  }
  return cleanMetricRangeMap(simplified);
};

export const expandSimplifiedRangeMapForStorage = (
  rangeMap: MetricRangeMap | undefined
): MetricRangeMap | undefined => {
  const simplified = collapseRangeMapForSimplifiedEditing(rangeMap);
  if (!simplified) return undefined;

  const expanded: MetricRangeMap = {};
  const primary = simplified.value;
  const countRange = simplified.count;
  const varianceRange = simplified.variance;

  if (primary) {
    PRIMARY_RANGE_KEYS.forEach((key) => {
      expanded[key] = { ...primary };
    });
  }

  if (countRange) {
    expanded.count = { ...countRange };
  }

  if (varianceRange) {
    VARIANCE_STDDEV_RANGE_KEYS.forEach((key) => {
      expanded[key] = { ...varianceRange };
    });
  }

  return cleanMetricRangeMap(expanded);
};

export const collapseMetricRuleSetForSimplifiedEditing = (
  ruleSet: MetricRuleSet | undefined | null
): MetricRuleSet | undefined => {
  const cleaned = cleanMetricRuleSet(ruleSet);
  if (!cleaned) return undefined;

  const collapsed: MetricRuleSet = {
    warningRange: collapseRangeMapForSimplifiedEditing(cleaned.warningRange),
    colorRange: collapseRangeMapForSimplifiedEditing(cleaned.colorRange),
    notificationRange: collapseRangeMapForSimplifiedEditing(cleaned.notificationRange),
    healthStatusRange: collapseRangeMapForSimplifiedEditing(cleaned.healthStatusRange),
    benchmarkRange: cleanBenchmarkBandRangeMap(cleaned.benchmarkRange),
    notificationLogic: cleaned.notificationLogic,
    healthStatusLogic: cleaned.healthStatusLogic,
  };

  return cleanMetricRuleSet(collapsed);
};

export const expandMetricRuleSetForSimplifiedStorage = (
  ruleSet: MetricRuleSet | undefined | null
): MetricRuleSet | undefined => {
  const cleaned = cleanMetricRuleSet(ruleSet);
  if (!cleaned) return undefined;

  const expanded: MetricRuleSet = {
    warningRange: expandSimplifiedRangeMapForStorage(cleaned.warningRange),
    colorRange: expandSimplifiedRangeMapForStorage(cleaned.colorRange),
    notificationRange: expandSimplifiedRangeMapForStorage(cleaned.notificationRange),
    healthStatusRange: expandSimplifiedRangeMapForStorage(cleaned.healthStatusRange),
    benchmarkRange: cleanBenchmarkBandRangeMap(cleaned.benchmarkRange),
    notificationLogic: cleaned.notificationLogic,
    healthStatusLogic: cleaned.healthStatusLogic,
  };

  return cleanMetricRuleSet(expanded);
};

export const extractMetricRules = (configurations: any): MetricRuleSetMap => {
  return cleanMetricRuleSetMap(configurations?.metricRules) || {};
};

export const extractAreaTypeMetricRules = (configurations: any): AreaTypeMetricRuleSetMap => {
  return cleanAreaTypeMetricRuleSetMap(configurations?.areaTypeMetricRules) || {};
};

const extractLegacyMetricLimits = (configurations: any): Record<string, MetricRangeMap> => {
  const raw = configurations?.metricLimits;
  if (!raw || typeof raw !== 'object') return {};

  const cleaned: Record<string, MetricRangeMap> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([metricKey, value]) => {
    const next = cleanMetricRangeMap(value);
    if (next) {
      cleaned[metricKey] = next;
    }
  });
  return cleaned;
};

const mergeRangeMaps = (...maps: Array<MetricRangeMap | undefined>): MetricRangeMap | undefined => {
  const merged: MetricRangeMap = {};

  maps.forEach((map) => {
    if (!map) return;

    Object.entries(map).forEach(([rangeKey, range]) => {
      if (!range || typeof range !== 'object') return;

      const prev = merged[rangeKey] || {};
      const next: MetricRange = { ...prev };

      if (hasOwn(range, 'min')) {
        next.min = range.min ?? null;
      }
      if (hasOwn(range, 'max')) {
        next.max = range.max ?? null;
      }

      const hasMin = hasOwn(next, 'min');
      const hasMax = hasOwn(next, 'max');
      if (!hasMin && !hasMax) return;
      if ((next.min ?? null) === null && (next.max ?? null) === null) return;

      merged[rangeKey] = next;
    });
  });

  return Object.keys(merged).length > 0 ? merged : undefined;
};

const normalizeAreaTypeIdKey = (areaTypeId?: number | string | null): string | null => {
  if (areaTypeId === null || areaTypeId === undefined) return null;
  const parsed = Number(areaTypeId);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return String(Math.trunc(parsed));
};

export const getMetricRuleSetForKey = (configurations: any, metricKey: string): MetricRuleSet | undefined => {
  const map = extractMetricRules(configurations);
  return map[metricKey];
};

export const getAreaTypeMetricRuleSetForKey = (
  configurations: any,
  areaTypeId: number | string | null | undefined,
  metricKey: string
): MetricRuleSet | undefined => {
  const key = normalizeAreaTypeIdKey(areaTypeId);
  if (!key) return undefined;
  const areaTypeMap = extractAreaTypeMetricRules(configurations);
  return areaTypeMap[key]?.[metricKey];
};

export const getLegacyWarningRangeForKey = (configurations: any, metricKey: string): MetricRangeMap | undefined => {
  const map = extractLegacyMetricLimits(configurations);
  return map[metricKey];
};

export const resolveFieldRange = (
  rangeMap: MetricRangeMap | undefined,
  fieldKey: string
): MetricRange | undefined => {
  if (!rangeMap) return undefined;

  const normalized = fieldKey === 'average' ? 'avg' : fieldKey;
  let candidates: string[] = [normalized];

  if (normalized === 'avg') {
    candidates = ['avg', 'average', 'value'];
  } else if (['min', 'max', 'sum', 'median'].includes(normalized)) {
    candidates = [normalized, 'value'];
  } else if (normalized === 'variance' || normalized === 'stddev') {
    candidates = [normalized, 'variance', 'stddev'];
  } else if (normalized === 'count') {
    candidates = ['count'];
  }

  for (const key of candidates) {
    if (rangeMap[key]) {
      return rangeMap[key];
    }
  }

  return undefined;
};

export const resolveWarningRangesForMetric = (metric: any, area?: any): MetricRangeMap | undefined => {
  const metricKey = metric?.key;
  const areaTypeId = area?.areaType?.id ?? area?.areaTypeId ?? null;
  if (!metricKey) return cleanMetricRangeMap(metric?.warningRanges);

  const areaTypeKey = normalizeAreaTypeIdKey(areaTypeId);
  const areaTypeRuleDefaults = areaTypeKey
    ? cleanMetricRangeMap(
        metric?.areaTypeRuleDefaults?.[areaTypeKey]?.warningRange ??
        metric?.areaTypeRuleDefaults?.[areaTypeKey]?.warningRanges
      )
    : undefined;

  return mergeRangeMaps(
    cleanMetricRangeMap(metric?.warningRanges),
    areaTypeRuleDefaults
  );
};

export const resolveColorRangesForMetric = (metric: any, area?: any): MetricRangeMap | undefined => {
  const metricKey = metric?.key;
  const areaTypeId = area?.areaType?.id ?? area?.areaTypeId ?? null;
  if (!metricKey) return cleanMetricRangeMap(metric?.colorRanges);

  const areaTypeKey = normalizeAreaTypeIdKey(areaTypeId);
  const areaTypeRuleDefaults = areaTypeKey
    ? cleanMetricRangeMap(
        metric?.areaTypeRuleDefaults?.[areaTypeKey]?.colorRange ??
        metric?.areaTypeRuleDefaults?.[areaTypeKey]?.colorRanges
      )
    : undefined;

  return mergeRangeMaps(
    cleanMetricRangeMap(metric?.colorRanges),
    areaTypeRuleDefaults
  );
};

export const resolveBenchmarkRangesForMetric = (metric: any, area?: any): MetricRangeMap | undefined => {
  const metricKey = metric?.key;
  const areaTypeId = area?.areaType?.id ?? area?.areaTypeId ?? null;
  if (!metricKey) {
    return cleanBenchmarkBandRangeMap(metric?.benchmarkRange ?? metric?.benchmarkRanges);
  }

  const areaTypeKey = normalizeAreaTypeIdKey(areaTypeId);
  const areaTypeRuleDefaults = areaTypeKey
    ? cleanBenchmarkBandRangeMap(
        metric?.areaTypeRuleDefaults?.[areaTypeKey]?.benchmarkRange ??
          metric?.areaTypeRuleDefaults?.[areaTypeKey]?.benchmarkRanges
      )
    : undefined;

  const merged = mergeRangeMaps(
    cleanBenchmarkBandRangeMap(metric?.benchmarkRange ?? metric?.benchmarkRanges),
    areaTypeRuleDefaults
  );

  return cleanBenchmarkBandRangeMap(merged);
};

export const isMetricRuleSetEmpty = (ruleSet: MetricRuleSet | undefined | null): boolean => {
  if (!ruleSet) return true;
  return !cleanMetricRuleSet(ruleSet);
};

export const buildConfigurationsWithMetricRule = (
  configurations: any,
  metricKey: string,
  nextRuleSet: MetricRuleSet | null
): Record<string, any> => {
  const nextConfigurations: Record<string, any> = {
    ...(configurations || {}),
  };

  const currentRules = extractMetricRules(nextConfigurations);
  const cleanedRuleSet = cleanMetricRuleSet(nextRuleSet);

  if (cleanedRuleSet) {
    currentRules[metricKey] = cleanedRuleSet;
  } else {
    delete currentRules[metricKey];
  }

  if (Object.keys(currentRules).length > 0) {
    nextConfigurations.metricRules = currentRules;
  } else {
    delete nextConfigurations.metricRules;
  }

  return nextConfigurations;
};

export const buildConfigurationsWithAreaTypeMetricRule = (
  configurations: any,
  areaTypeId: number | string,
  metricKey: string,
  nextRuleSet: MetricRuleSet | null
): Record<string, any> => {
  const nextConfigurations: Record<string, any> = {
    ...(configurations || {}),
  };

  const key = normalizeAreaTypeIdKey(areaTypeId);
  if (!key) {
    return nextConfigurations;
  }

  const currentAreaTypeRules = extractAreaTypeMetricRules(nextConfigurations);
  const currentMetricRulesForAreaType = currentAreaTypeRules[key] || {};
  const cleanedRuleSet = cleanMetricRuleSet(nextRuleSet);

  if (cleanedRuleSet) {
    currentMetricRulesForAreaType[metricKey] = cleanedRuleSet;
  } else {
    delete currentMetricRulesForAreaType[metricKey];
  }

  if (Object.keys(currentMetricRulesForAreaType).length > 0) {
    currentAreaTypeRules[key] = currentMetricRulesForAreaType;
  } else {
    delete currentAreaTypeRules[key];
  }

  if (Object.keys(currentAreaTypeRules).length > 0) {
    nextConfigurations.areaTypeMetricRules = currentAreaTypeRules;
  } else {
    delete nextConfigurations.areaTypeMetricRules;
  }

  return nextConfigurations;
};

export const formatRangeLabel = (range?: MetricRange) => {
  const min = range?.min ?? null;
  const max = range?.max ?? null;
  if (min === null && max === null) return '—';
  const minLabel = min === null ? '—' : min;
  const maxLabel = max === null ? '—' : max;
  return `${minLabel} to ${maxLabel}`;
};

export const getMetricRuleDefaultsFromMetric = (metric: any): MetricRuleSet => {
  return {
    warningRange: cleanMetricRangeMap(metric?.warningRanges),
    colorRange: cleanMetricRangeMap(metric?.colorRanges),
    notificationRange: cleanMetricRangeMap(metric?.notificationRanges),
    healthStatusRange: cleanMetricRangeMap(metric?.healthStatusRanges),
    benchmarkRange: cleanBenchmarkBandRangeMap(metric?.benchmarkRange ?? metric?.benchmarkRanges),
    notificationLogic: metric?.notificationLogic || undefined,
    healthStatusLogic: metric?.healthStatusLogic || undefined,
  };
};
